import { mapRequestErrorToCode } from './helpers';
import { SncrService } from './sncrService';
import { SncrFeedback, SncrNumberingRequestItem } from './types';

interface RequestSncrQuotaParams {
  doctorHash: string;
  items: SncrNumberingRequestItem[];
  /** Session id do Gov.br quando a requisição segue logo após uma autenticação. */
  sessionId?: string;
  /** Recebe cada transição de estado da requisição (processando → resultado/erro). */
  onFeedback(feedback: SncrFeedback): void;
  /** Notifica se a sessão Gov.br passou a estar ativa (`true`) ou inválida (`false`). */
  onSessionChange(active: boolean): void;
}

/**
 * Executa a requisição de cotas SNCR de ponta a ponta, compartilhada pelos dois
 * fluxos que a disparam: o atalho "solicitar tudo na quantidade padrão" do card sem saldo
 * ({@link useSncrRequestShortcut}) e a seleção manual do painel
 * ({@link useSncrNumberingFlow}).
 *
 * Sequência: emite `processing` → (se houver `sessionId`) autentica no Gov.br →
 * chama `addDoctorQuota` → emite `result` ou `error`. Erros são mapeados por
 * {@link mapRequestErrorToCode}; um `auth_expired` durante a emissão marca a
 * sessão como inválida via `onSessionChange(false)`. Alertas por item
 * (incluindo `UNKNOWN_ERROR` e `ANVISA_UNAUTHORIZED`) são resolvidos card a
 * card pelo resultado normal; o modal genérico de erro só é usado quando a
 * própria chamada `addDoctorQuota` lança uma exceção (rede, HTTP genérico).
 *
 * Os efeitos colaterais específicos de cada fluxo (spinners, modo do painel)
 * ficam nos respectivos hooks; aqui só há a orquestração comum.
 */
export async function requestSncrQuota({
  doctorHash,
  items,
  sessionId,
  onFeedback,
  onSessionChange,
}: RequestSncrQuotaParams): Promise<void> {
  onFeedback({ kind: 'processing', items });

  if (sessionId) {
    try {
      await SncrService.authenticateDoctor(doctorHash, sessionId);
      onSessionChange(true);
    } catch (error) {
      onSessionChange(false);
      onFeedback({ kind: 'error', code: mapRequestErrorToCode(error) });
      return;
    }
  }

  try {
    const result = await SncrService.addDoctorQuota(doctorHash, items);
    onFeedback({ kind: 'result', result });
  } catch (error) {
    const code = mapRequestErrorToCode(error, true);
    if (code === 'auth_expired') onSessionChange(false);
    onFeedback({ kind: 'error', code });
  }
}
